import type { CommentStackSiderViewConfigType } from '@/features/comment/types/stack-sider-view';
import type { WorkflowStackSiderViewConfigType } from '@/features/workflow/types/stack-sider-view';
import {
  type Dispatch,
  type PropsWithChildren,
  createContext,
  useContext,
  useReducer,
} from 'react';

export type StackSiderViewType = 'comment' | 'workflow';

type StackSiderViewConfigMap = {
  comment: CommentStackSiderViewConfigType;
  workflow: WorkflowStackSiderViewConfigType;
};

type StackSiderTargetType =
  | {
      type: 'project';
      projectId: string;
    }
  | {
      type: 'none';
    };

type StackSiderView<T extends StackSiderViewType> = {
  siderType: T;
  config: StackSiderViewConfigMap[T];
};

type Stack = {
  [K in StackSiderViewType]: StackSiderView<K>;
}[StackSiderViewType];

type State = {
  stack: Stack[];
  target: StackSiderTargetType;
};

type Action<T extends StackSiderViewType> =
  | { type: 'PUSH'; payload: StackSiderView<T>; target: StackSiderTargetType }
  | { type: 'SET'; payload: StackSiderView<T>[]; target?: StackSiderTargetType }
  | { type: 'POP' }
  | { type: 'CLEAR' };

const StackSiderViewContext = createContext<{
  state: State;
  dispatch: Dispatch<Action<StackSiderViewType>>;
} | null>(null);

const initialState: State = {
  stack: [],
  target: { type: 'none' },
};

const reducer = (state: State, action: Action<StackSiderViewType>): State => {
  switch (action.type) {
    case 'PUSH':
      return {
        ...state,
        stack: state.stack.concat(action.payload as Stack),
        target: action.target ?? state.target,
      };
    case 'SET':
      return {
        ...state,
        stack: action.payload as Stack[],
        target: action.target ?? state.target,
      };
    case 'POP':
      return {
        ...state,
        stack: state.stack.slice(0, -1),
      };
    case 'CLEAR':
      return initialState;
    default:
      throw new Error('Unhandled action type');
  }
};

export const useStackSiderView = () => {
  const context = useContext(StackSiderViewContext);
  if (!context) {
    throw new Error('useSiderState must be used within SiderStateProvider.');
  }

  return context;
};

type StackSiderViewHookReturnType<T extends StackSiderViewType> = {
  list: StackSiderView<T>[];
  lastItem: StackSiderView<T> | undefined;
  push: ({
    payload,
    target,
  }: {
    payload: Omit<StackSiderView<T>, 'siderType'>;
    target: StackSiderTargetType;
  }) => void;
  set: (params: {
    payload: StackSiderView<T>[];
    target: StackSiderTargetType;
  }) => void;
  updateLastItem: (
    payload: Partial<Omit<StackSiderView<T>, 'siderType'>>,
  ) => void;
  pop: () => void;
  clear: () => void;
  target: StackSiderTargetType;
};

export const createStackSiderViewHook =
  <T extends StackSiderViewType>(siderType: T) =>
  (): StackSiderViewHookReturnType<T> => {
    const { state, dispatch } = useStackSiderView();
    const { stack, target } = state;
    const filteredState = stack.filter(
      (s) => s.siderType === siderType,
    ) as StackSiderView<T>[];

    return {
      list: filteredState,
      lastItem: filteredState.at(-1),
      push: ({
        payload,
        target,
      }: {
        payload: Omit<StackSiderView<T>, 'siderType'>;
        target: StackSiderTargetType;
      }) => {
        dispatch({
          type: 'PUSH',
          payload: { ...payload, siderType } as Stack,
          target,
        });
      },
      set: ({
        payload,
        target,
      }: { payload: StackSiderView<T>[]; target: StackSiderTargetType }) =>
        dispatch({ type: 'SET', payload: payload as Stack[], target }),
      updateLastItem: (payload: Partial<StackSiderView<T>>) =>
        dispatch({
          type: 'SET',
          payload: [
            ...stack.slice(0, -1),
            { ...stack.at(-1), ...payload } as Stack,
          ],
        }),
      pop: () => dispatch({ type: 'POP' }),
      clear: () => dispatch({ type: 'CLEAR' }),
      target,
    };
  };

export const StackSiderViewProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <StackSiderViewContext.Provider value={{ state, dispatch }}>
      {children}
    </StackSiderViewContext.Provider>
  );
};
